// Shared Swiper initialization helper.
// Webflow markup convention — every swiper uses these data-attributes,
// where {key} is unique per swiper (e.g. "is--blog", "principles", "mission"):
//
//   data-swiper-group='{key}'         ← outer wrapper (also holds nav + pagination)
//     data-swiper-wrap='{key}'        ← .swiper element (Swiper attaches here)
//     data-swiper-prev='{key}'        ← prev button
//     data-swiper-next='{key}'        ← next button
//     data-swiper-pagination='{key}'  ← pagination element
//
// Requires: Swiper loaded globally on the Webflow page.

export function debounce(fn, wait = 200) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
}

/**
 * Initialize a single Swiper. Returns the instance, or null if not found.
 *
 * Defaults match the Voyantis convention: auto-width slides, mobile-only
 * (disabled at >=992px), with bullet pagination, prev/next nav, and keyboard
 * support. Pass `extraConfig` to override or add Swiper options.
 */
export function createSwiper(key, extraConfig = {}) {
  const swiperGroup = document.querySelector(`[data-swiper-group='${key}']`);
  if (!swiperGroup) return null;

  const swiperWrap = swiperGroup.querySelector(`[data-swiper-wrap='${key}']`);
  if (!swiperWrap) return null;

  // Destroy any existing instance (safe re-init on resize)
  if (swiperWrap.swiper) {
    try {
      swiperWrap.swiper.destroy(true, true);
    } catch (e) {
      // no-op
    }
  }

  const prevButton = swiperGroup.querySelector(`[data-swiper-prev='${key}']`);
  const nextButton = swiperGroup.querySelector(`[data-swiper-next='${key}']`);
  const paginationEl = swiperGroup.querySelector(`[data-swiper-pagination='${key}']`);

  return new Swiper(swiperWrap, {
    slidesPerView: 'auto',
    speed: 600,
    grabCursor: true,
    breakpoints: {
      480: { slidesPerView: 'auto' },
      992: { enabled: false },
      ...extraConfig.breakpoints,
    },
    navigation: { nextEl: nextButton, prevEl: prevButton },
    pagination: { el: paginationEl, type: 'bullets', clickable: true },
    keyboard: { enabled: true, onlyInViewport: false },
    ...extraConfig,
  });
}

/**
 * Run `initFn` once now, then re-run it whenever the viewport WIDTH changes
 * (debounced 250ms). Use this to wrap one or more `createSwiper` calls so the
 * swipers re-initialize cleanly across breakpoints.
 *
 *   withResize(() => {
 *     createSwiper('is--blog');
 *     createSwiper('is--articles');
 *   });
 */
export function withResize(initFn) {
  initFn();

  let lastWidth = window.innerWidth;
  const onResize = debounce(() => {
    if (window.innerWidth === lastWidth) return;
    lastWidth = window.innerWidth;
    initFn();
  }, 250);

  window.addEventListener('resize', onResize, { passive: true });
}
